import React, { useMemo, useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { IconPlus, IconPencil, IconUserOff, IconUserCheck } from '@tabler/icons-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { useLoading } from '../context/LoadingContext';
import ConfirmModal from '../components/ConfirmModal';
import SelectField from '../components/SelectField';

const WRITES_TO_SHEET = Boolean(process.env.REACT_APP_APPS_SCRIPT_URL);

const STATUS_STYLES = {
  Active: 'bg-success-bg text-success-tx',
  Inactive: 'bg-neutral-bg text-neutral-tx',
};

const PAGE_SIZE = 5;

const EMPTY_FORM = {
  name: '',
  role: '',
  gmail: '',
  contact_number: '',
  timezone: '',
  date_of_hire: '',
  birthdayDate: '', // a full date picked in the form — only month/day is kept, same as Holidays
  address: '',
  isAdmin: false,
  holidayListId: '',
};

// Same inline re-auth pattern as Clients/Holidays/Announcements — a quick
// Google sign-in right in the modal footer when the token's gone stale.
function ReauthFooter({ setIdToken, onCancel }) {
  const { showError } = useToast();
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted text-center">Your session needs a quick refresh before this can be saved.</p>
      <GoogleLogin
        onSuccess={(credentialResponse) => setIdToken(credentialResponse.credential)}
        onError={() => showError('Google sign-in failed — please try again.')}
      />
      <button onClick={onCancel} className="text-sm font-medium text-muted hover:text-ink transition-colors">
        Cancel
      </button>
    </div>
  );
}

export default function Employees() {
  const {
    users,
    tzOffsets,
    holidayLists,
    createEmployee,
    updateEmployee,
    deactivateEmployee,
    reactivateEmployee,
    setIdToken,
    isIdTokenValid,
  } = useApp();
  const { showSuccess, showError } = useToast();
  const { withLoading } = useLoading();
  const needsReauth = WRITES_TO_SHEET && !isIdTokenValid;

  // Sheets without the status column yet are treated as all-Active — same
  // tolerant default Code.gs uses when reading this column.
  const employees = useMemo(
    () => users.map((u) => ({ ...u, status: u.status || 'Active', resignation_date: u.resignation_date || '' })),
    [users]
  );

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) => {
        if (a.status !== b.status) return a.status === 'Active' ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [employees]
  );

  const timezoneOptions = useMemo(() => Object.keys(tzOffsets).sort(), [tzOffsets]);

  // --- Pagination ---
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(sortedEmployees.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedEmployees = sortedEmployees.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const [formOpen, setFormOpen] = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const openCreate = () => {
    setEditingUuid(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (emp) => {
    // Birthday is freeform "Month D" text (e.g. "July 28") with no real
    // year — same convention as Holidays, so reuse the same placeholder-
    // year trick to seed a date input.
    let birthdayDate = '';
    const match = String(emp.birthday || '').match(/^([A-Za-z]+)\.?\s+(\d{1,2})/);
    if (match) {
      const d = new Date(`${match[1]} ${match[2]}, 2001`);
      if (!isNaN(d.getTime())) {
        birthdayDate = `2001-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }
    setEditingUuid(emp.uuid);
    setForm({
      name: emp.name || '',
      role: emp.role || '',
      gmail: emp.gmail || '',
      contact_number: emp.contact_number || '',
      timezone: emp.timezone || '',
      date_of_hire: emp.date_of_hire || '',
      birthdayDate,
      address: emp.address || '',
      isAdmin: Boolean(emp.isAdmin),
      holidayListId: emp.holiday_list_id || '',
    });
    setFormOpen(true);
  };

  const closeForm = () => setFormOpen(false);

  const confirmSave = async () => {
    if (!form.name.trim()) {
      showError('Give the employee a name.');
      return;
    }
    if (!form.gmail.trim()) {
      showError('A Gmail address is required — it\'s how they sign in.');
      return;
    }

    let birthday = '';
    if (form.birthdayDate) {
      const [, m, d] = form.birthdayDate.split('-');
      birthday = new Date(Date.UTC(2001, Number(m) - 1, Number(d))).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      });
    }

    const fields = {
      name: form.name.trim(),
      role: form.role.trim(),
      gmail: form.gmail.trim(),
      contactNumber: form.contact_number.trim(),
      timezone: form.timezone,
      dateOfHire: form.date_of_hire,
      birthday,
      address: form.address.trim(),
      isAdmin: form.isAdmin,
      holidayListId: form.holidayListId,
    };

    try {
      if (editingUuid) {
        await withLoading(() => updateEmployee({ targetUuid: editingUuid, fields }));
        showSuccess('Employee updated.');
      } else {
        await withLoading(() => createEmployee(fields));
        showSuccess('Employee added.');
      }
      closeForm();
    } catch (err) {
      showError(err.message || "Couldn't save that — please try again.");
    }
  };

  // --- Deactivate (soft delete) ---
  const [deactivateTarget, setDeactivateTarget] = useState(null); // uuid | null
  const [resignationDate, setResignationDate] = useState('');

  const openDeactivate = (uuid) => {
    setDeactivateTarget(uuid);
    setResignationDate('');
  };

  const confirmDeactivate = async () => {
    if (!resignationDate) {
      showError('Pick their resignation date.');
      return;
    }
    try {
      await withLoading(() => deactivateEmployee({ targetUuid: deactivateTarget, resignationDate }));
      showSuccess('Employee deactivated.');
      setDeactivateTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't deactivate that — please try again.");
    }
  };

  // --- Reactivate ---
  const [reactivateTarget, setReactivateTarget] = useState(null); // uuid | null

  const confirmReactivate = async () => {
    try {
      await withLoading(() => reactivateEmployee({ targetUuid: reactivateTarget }));
      showSuccess('Employee reactivated.');
      setReactivateTarget(null);
    } catch (err) {
      showError(err.message || "Couldn't reactivate that — please try again.");
    }
  };

  const deactivateTargetEmployee = employees.find((e) => e.uuid === deactivateTarget);
  const reactivateTargetEmployee = employees.find((e) => e.uuid === reactivateTarget);

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <p className="text-sm text-muted">
            Manage the team roster. Deactivating keeps their history but marks them as no longer active.
          </p>
          <button
            onClick={openCreate}
            className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl font-semibold text-white bg-gold hover:bg-bronze transition-colors text-sm whitespace-nowrap flex-shrink-0"
          >
            <IconPlus size={16} stroke={2} />
            New
          </button>
        </div>

        <div className="mt-6 bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-light-gold text-left">
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Name</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Role</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-ink text-xs uppercase tracking-wide whitespace-nowrap">Email</th>
                  <th className="px-3 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pagedEmployees.map((emp) => (
                  <tr
                    key={emp.uuid}
                    className={`border-t border-border hover:bg-card-tint transition-colors ${
                      emp.status === 'Inactive' ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <p className="font-medium text-ink">{emp.name}</p>
                      {emp.isAdmin && <p className="text-[10px] text-bronze uppercase tracking-wide">Admin</p>}
                    </td>
                    <td className="px-3 py-2.5 text-ink whitespace-nowrap">{emp.role || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-full font-medium px-2.5 py-0.5 text-[11px] tracking-wide ${STATUS_STYLES[emp.status]}`}
                      >
                        {emp.status}
                      </span>
                      {emp.status === 'Inactive' && emp.resignation_date && (
                        <p className="text-[10px] text-muted mt-0.5">Resigned {emp.resignation_date}</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-muted whitespace-nowrap">{emp.gmail || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openEdit(emp)}
                          aria-label="Edit"
                          className="p-1.5 rounded-lg text-muted hover:text-ink hover:bg-card-tint transition-colors"
                        >
                          <IconPencil size={16} stroke={1.8} />
                        </button>
                        {emp.status === 'Active' ? (
                          <button
                            onClick={() => openDeactivate(emp.uuid)}
                            aria-label="Deactivate"
                            title="Deactivate"
                            className="p-1.5 rounded-lg text-muted hover:text-error-tx hover:bg-card-tint transition-colors"
                          >
                            <IconUserOff size={16} stroke={1.8} />
                          </button>
                        ) : (
                          <button
                            onClick={() => setReactivateTarget(emp.uuid)}
                            aria-label="Reactivate"
                            title="Reactivate"
                            className="p-1.5 rounded-lg text-muted hover:text-success-tx hover:bg-card-tint transition-colors"
                          >
                            <IconUserCheck size={16} stroke={1.8} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedEmployees.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-10 text-center text-sm text-muted">
                      No employees yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-3 sm:px-6 py-2.5 border-t border-border flex items-center justify-between gap-2">
              <p className="text-xs text-muted">
                Page {safePage} of {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ‹ Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="px-2 py-1 rounded-lg border border-border text-xs font-medium text-ink hover:bg-card-tint transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next ›
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={formOpen}
        title={editingUuid ? 'Edit employee' : 'New employee'}
        confirmLabel="Save"
        onConfirm={confirmSave}
        onCancel={closeForm}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={closeForm} /> : undefined}
      >
        <div>
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Name</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Jordan Cruz"
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Role</label>
            <input
              type="text"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              placeholder="e.g. Call Specialist"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Gmail</label>
            <input
              type="email"
              value={form.gmail}
              onChange={(e) => setForm((f) => ({ ...f, gmail: e.target.value }))}
              placeholder="name@gmail.com"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Contact number</label>
            <input
              type="text"
              value={form.contact_number}
              onChange={(e) => setForm((f) => ({ ...f, contact_number: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Timezone</label>
            <SelectField value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}>
              <option value="">Select...</option>
              {timezoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </SelectField>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Date hired</label>
            <input
              type="date"
              value={form.date_of_hire}
              onChange={(e) => setForm((f) => ({ ...f, date_of_hire: e.target.value }))}
              className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Birthday</label>
            <input
              type="date"
              value={form.birthdayDate}
              onChange={(e) => setForm((f) => ({ ...f, birthdayDate: e.target.value }))}
              className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
            />
            <p className="text-[11px] text-muted mt-1">Only the month/day is used.</p>
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Address</label>
          <textarea
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            rows={2}
            className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold resize-none"
          />
        </div>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Holiday calendar</label>
          <SelectField
            value={form.holidayListId}
            onChange={(e) => setForm((f) => ({ ...f, holidayListId: e.target.value }))}
          >
            <option value="">No calendar assigned</option>
            {holidayLists.map((l) => (
              <option key={l.list_id} value={l.list_id}>
                {l.name}
              </option>
            ))}
          </SelectField>
          <p className="text-[11px] text-muted mt-1">Which country's holidays show on their own Leaves calendar.</p>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={form.isAdmin}
            onChange={(e) => setForm((f) => ({ ...f, isAdmin: e.target.checked }))}
          />
          Admin
        </label>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        title={deactivateTargetEmployee ? `Deactivate ${deactivateTargetEmployee.name}?` : 'Deactivate this employee?'}
        confirmLabel="Deactivate"
        onConfirm={confirmDeactivate}
        onCancel={() => setDeactivateTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setDeactivateTarget(null)} /> : undefined}
      >
        <p>They'll stay in the roster (soft delete) but be marked inactive and blocked from signing in. This needs their resignation date.</p>
        <div className="mt-3">
          <label className="block text-xs font-medium text-muted uppercase tracking-wide mb-1">Resignation date</label>
          <input
            type="date"
            value={resignationDate}
            onChange={(e) => setResignationDate(e.target.value)}
            className="w-full border border-border rounded-lg px-2 py-1.5 text-sm text-ink"
          />
        </div>
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(reactivateTarget)}
        title={reactivateTargetEmployee ? `Reactivate ${reactivateTargetEmployee.name}?` : 'Reactivate this employee?'}
        confirmLabel="Reactivate"
        onConfirm={confirmReactivate}
        onCancel={() => setReactivateTarget(null)}
        footer={needsReauth ? <ReauthFooter setIdToken={setIdToken} onCancel={() => setReactivateTarget(null)} /> : undefined}
      >
        <p>Marks them active again, clears their resignation date, and lets them sign in again.</p>
      </ConfirmModal>
    </>
  );
}
